import { Box, Button, CircularProgress, Typography, alpha } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import snackbarUtil from '../../app/snackbar/snackbarUtil'

type DropzoneProps = {
  removeHandle: (fileId: string) => void
  viewHandle: (fileId: string) => void
  uploadHandle: (file: File) => Promise<string>
  isDisabled?: boolean
  isRequired?: boolean
  fileId?: string
}
const SUPPORTED_EXTENSIONS = '.jpg, .jpeg, .png, .gif, .pdf, .doc, .docx, .xls, .xlsx, .csv, .txt'

function extractFileName(fullString: string): string {
  const parts = fullString.split('.')
  parts.shift()
  parts.shift()
  return parts.join('.')
}

const Dropzone = ({
  fileId,
  isDisabled,
  isRequired,
  viewHandle,
  removeHandle,
  uploadHandle,
}: DropzoneProps) => {
  const [isUploading, setIsUploading] = useState(false)
  const [currFileId, setCurrFileId] = useState<string | undefined>(fileId)

  useEffect(() => {
    setCurrFileId(fileId)
  }, [fileId])
  const [uploadedFile, setUploadedFile] = useState<File | undefined>(undefined)

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (isDisabled) return
      const file = acceptedFiles[0]
      setIsUploading(true)
      try {
        const uploadedFileId = await uploadHandle(file)
        setCurrFileId(uploadedFileId)
        setUploadedFile(file)
      } catch (error) {
        console.error('File upload failed:', error)
      } finally {
        setIsUploading(false)
      }
    },
    [uploadHandle, isDisabled]
  )

  const onDropRejected = useCallback(
    (fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        snackbarUtil.error(
          `Unsupported file type. Supported formats: ${SUPPORTED_EXTENSIONS}`
        )
      }
    },
    []
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    maxFiles: 1,
    noClick: isDisabled,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      'text/plain': ['.txt'],
    },
    noKeyboard: isDisabled,
    noDrag: isDisabled,
  })

  return (
    <Box
      {...getRootProps()}
      sx={(theme) => ({
        border: !isDragActive ? `2px dashed ${theme.palette.primary.main}` : '2px dashed #cccccc',
        backgroundColor: !isDragActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
        borderRadius: 2,
        padding: 4,
        textAlign: 'center',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        ...(isRequired && { border: `2px dashed ${theme.palette.error.main}` }),
      })}
    >
      {isUploading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : currFileId ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography>{`Uploaded file: ${extractFileName(currFileId)}`}</Typography>
          <Box sx={{ display: 'flex' }}>
            {!isDisabled && (
              <>
                <Button
                  color='primary'
                  onClick={(e) => {
                    e.stopPropagation()
                    removeHandle(fileId ?? '')
                    setCurrFileId(undefined)
                    setUploadedFile(undefined)
                  }}
                  sx={{ mt: 1 }}
                  disabled={isDisabled}
                >
                  Remove
                </Button>
              </>
            )}
            <Button
              color='primary'
              onClick={(e) => {
                e.stopPropagation()
                viewHandle(fileId ?? '')
              }}
              sx={{ mt: 1 }}
            >
              View
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          {isDragActive ? (
            <Typography>Drop the file here ...</Typography>
          ) : (
            <Typography>Drag & drop a file here, or</Typography>
          )}
          <Button
            component='span'
            variant='contained'
            color='primary'
            sx={{ mt: 2 }}
            disabled={isDisabled}
          >
            Browse
            <input
              {...getInputProps()}
              style={{ display: 'none' }} // Hide the input element
            />
          </Button>
        </>
      )}
    </Box>
  )
}

export default Dropzone
